"use client";

import { useState } from "react";
import { Icon } from "@iconify/react";
import { uiIcons } from "@/config/icons";
import { PDFDocument, degrees } from "pdf-lib";
import { FileUploader } from "@/components/ui/FileUploader";
import { usePathname } from "next/navigation";
import { getLocaleFromPathname } from "@/lib/i18n";
import { getToolUiCopy } from "@/lib/tool-ui-copy";
import { getPdfToolCopy } from "@/lib/pdf-tool-copy";
import { getEditorExtraCopy } from "@/lib/editor-extra-copy";
import { tools } from "@/config/tools";
import { getLocalizedTool } from "@/lib/tool-localization";

export function RotatePdf() {
  const [file, setFile] = useState<File | null>(null);
  const [pageCount, setPageCount] = useState(0);
  const [rotation, setRotation] = useState(90);
  const [isProcessing, setIsProcessing] = useState(false);
  const pathname = usePathname() || "/";
  const locale = getLocaleFromPathname(pathname);
  const ui = getToolUiCopy(locale);
  const copy = getPdfToolCopy(locale);
  const editorExtra = getEditorExtraCopy(locale);
  const localizedTool = getLocalizedTool(
    tools.find((item) => item.id === "rotate-pdf") ?? tools[0],
    locale
  );

  const handleFilesSelected = async (files: File[]) => {
    if (files.length > 0) {
      const selectedFile = files[0];
      setFile(selectedFile);

      try {
        const fileBytes = await selectedFile.arrayBuffer();
        const pdf = await PDFDocument.load(fileBytes);
        setPageCount(pdf.getPageCount());
      } catch (error) {
        console.error("Error loading PDF:", error);
      }
    }
  };

  const rotatePDF = async () => {
    if (!file) return;

    setIsProcessing(true);

    try {
      const fileBytes = await file.arrayBuffer();
      const pdfDoc = await PDFDocument.load(fileBytes);
      const pages = pdfDoc.getPages();

      pages.forEach((page) => {
        const currentRotation = page.getRotation().angle;
        page.setRotation(degrees(currentRotation + rotation));
      });

      const pdfBytes = await pdfDoc.save();
      const blob = new Blob([pdfBytes as BlobPart], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `rotated-${file.name}`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error rotating PDF:", error);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="space-y-8">
      <div className="rounded-[20px] border border-positivus-dark/10 bg-positivus-green/10 p-6">
         <div className="flex items-start gap-4">
           <div className="rounded-full bg-positivus-green/20 p-2">
             <Icon icon="mdi:information-variant" className="h-6 w-6 text-positivus-dark" />
           </div>
           <div className="text-sm text-positivus-dark">
             <p className="font-medium text-lg mb-2">{localizedTool.name}</p>
             <p className="leading-relaxed">{localizedTool.description}</p>
           </div>
         </div>
       </div>

      {!file ? (
        <div className="space-y-4">
            <FileUploader 
              accept=".pdf" 
              onFilesSelected={handleFilesSelected}
            />
        </div>
      ) : (
        <div className="space-y-6">
          <div className="rounded-[30px] border-2 border-positivus-dark bg-white p-6 md:p-8">
            <div className="flex items-center gap-6">
              <div className="flex h-16 w-16 items-center justify-center rounded-[20px] bg-red-100 text-red-600 border border-red-200">
                <Icon icon={uiIcons.fileText} className="h-8 w-8" />
              </div>
              <div>
                <p className="font-bold text-xl text-positivus-dark">{file.name}</p>
                <div className="flex items-center gap-2 mt-1">
                   <p className="text-gray-500 font-medium">{pageCount} {copy.pages}</p>
                </div>
              </div>
              <button 
                onClick={() => setFile(null)}
                className="ml-auto text-gray-400 hover:text-red-500 p-2"
                title={ui.remove}
              >
                <Icon icon={uiIcons.refresh} className="h-6 w-6" />
              </button>
            </div>
          </div>

          <div>
            <label className="mb-3 block text-lg font-medium text-positivus-dark">{editorExtra.rotation}</label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[90, 180, 270, -90].map((angle) => (
                <button
                  key={angle}
                  onClick={() => setRotation(angle)}
                  className={`flex flex-col items-center gap-2 rounded-[20px] border-2 p-4 transition-all ${
                    rotation === angle
                      ? "border-positivus-dark bg-positivus-dark text-white shadow-[0_5px_0_0_#191A23]"
                      : "border-positivus-dark/20 hover:border-positivus-dark bg-white hover:bg-positivus-green/10"
                  }`}
                >
                  <Icon 
                    icon="mdi:rotate-right" 
                    className="h-8 w-8 mb-1"
                    style={{ transform: `rotate(${angle}deg)` }}
                  />
                  <span className="text-base font-bold">{angle > 0 ? `+${angle}°` : `${angle}°`}</span>
                </button>
              ))}
            </div>
          </div>

          <button
            onClick={rotatePDF}
            disabled={isProcessing}
            className="w-full rounded-[20px] bg-positivus-dark px-6 py-4 text-xl font-medium text-white hover:bg-positivus-green hover:text-positivus-dark transition-all shadow-[0_5px_0_0_#191A23] hover:shadow-none hover:translate-y-[2px] disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none disabled:hover:translate-y-0 disabled:hover:bg-positivus-dark disabled:hover:text-white"
          >
             {isProcessing ? (
                <div className="flex items-center justify-center gap-3">
                    <Icon icon="mdi:loading" className="h-6 w-6 animate-spin" />
                    <span>{ui.converting}</span>
                </div>
            ) : `${editorExtra.rotation} ${rotation}°`}
          </button>
        </div>
      )}
    </div>
  );
}
